import { useCallback, useEffect, useMemo, useState } from "react";

import { Box, Grid, Table } from "@mantine/core";

import { IconPlus } from "@tabler/icons-react";

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { Button, NumberFormatter, Text, Title } from "components";
import { IRootState } from "config/redux";
import { AuthorityConstants, ServiceConstants } from "constant";
import { CreditOptionsResponse, CreditRequestPayment, CreditResponse } from "types";

import { AppUtils, UserUtils } from "utils";

import CreditPaymentStructureListItem from "./list/item";

import CreditPaymentCreateModal from "./list/new";

const { ROLE_CR_C, ROLE_CR_U } = AuthorityConstants;
const { REQUEST_CREDIT_PAYMENT_CREATE_ID } = ServiceConstants;

interface CreditPaymentStructureProps {
  options: CreditOptionsResponse;
  credit: CreditResponse;
  readOnly?: boolean;
}

const CreditPaymentStructure = ({
  options,
  credit,
  readOnly = false,
}: CreditPaymentStructureProps) => {
  const { t } = useTranslation();

  const createServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_CREDIT_PAYMENT_CREATE_ID],
  );

  const [createModal, setCreateModal] = useState<boolean>(false);
  const [totalAmount, setTotalAmount] = useState<number | undefined>(undefined);
  const [tempPayments, setTempPayments] = useState<CreditRequestPayment[] | undefined>(undefined);

  useEffect(() => {
    setTempPayments(credit.payments);
  }, [credit]);

  useEffect(() => {
    recalculateTotalAmount();
  }, [tempPayments]);

  useEffect(() => {
    AppUtils.resolveServiceObject(createServiceObject, REQUEST_CREDIT_PAYMENT_CREATE_ID, (val) => {
      const temp = tempPayments ? [...tempPayments] : [];
      temp.push(val);
      setTempPayments(temp);
      setCreateModal(false);
    });
  }, [createServiceObject]);

  const recalculateTotalAmount = useCallback(() => {
    let total = 0;
    tempPayments?.forEach((el) => {
      total += el.amount;
    });
    setTotalAmount(total);
  }, [tempPayments]);

  const removePayment = useCallback(
    (id: number) => {
      setTempPayments((old) => old?.filter((el) => el.id !== id));
    },
    [tempPayments],
  );

  const rows = useMemo(() => {
    return tempPayments?.map((el, index) => (
      <CreditPaymentStructureListItem
        key={el.id}
        item={el}
        index={index}
        removePayment={removePayment}
        readOnly={readOnly}
      />
    ));
  }, [tempPayments, readOnly]);

  return (
    <Box>
      <Title order={5}>
        {totalAmount ? (
          <NumberFormatter
            prefix={t("credit.payment.title") + " ("}
            value={totalAmount}
            suffix={" CHF)"}
            fixedDecimalScale={true}
            decimalScale={2}
          />
        ) : (
          t("credit.payment.title")
        )}
      </Title>
      <Grid>
        <Grid.Col span={{ sm: 12, md: 8 }}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("credit.payment.type.label")}</Table.Th>
                <Table.Th>{t("credit.bank.label")}</Table.Th>
                <Table.Th>{t("credit.payment.amount.label")}</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows}
              {!readOnly && UserUtils.hasAnyAuthority([ROLE_CR_C, ROLE_CR_U]) && (
                <Table.Tr key={"credit-payment-new"}>
                  <Table.Td colSpan={4}>
                    <Button
                      size={"compact-xs"}
                      variant={"transparent"}
                      c={"var(--mantine-color-gray-6)"}
                      onClick={() => setCreateModal(true)}
                    >
                      <>
                        <Box mr={4}><IconPlus size={16} /></Box>
                        {t("credit.payment.new.action")}
                      </>
                    </Button>
                    <CreditPaymentCreateModal
                      creditId={credit.id}
                      opened={createModal}
                      onClose={() => setCreateModal(false)}
                      options={options}
                      t={t}
                    />
                  </Table.Td>
                </Table.Tr>
              )}
              <Table.Tr key={"credit-payment-empty"}>
                <Table.Td colSpan={4}></Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
          {totalAmount &&
          totalAmount > 0 &&
          credit.grantedCreditAmount &&
          credit.grantedCreditAmount > 0 &&
          totalAmount !== credit.grantedCreditAmount ? (
            <Text ta={"right"} c={"var(--mantine-color-error)"}>
              {t("credit.payment.diff")}
            </Text>
          ) : (
            <></>
          )}
        </Grid.Col>
      </Grid>
    </Box>
  );
};

export default CreditPaymentStructure;
